import { useRef, useState, useEffect } from "react";
import { DialogContent, Box as MuiBox, Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { Save, SwapHorizOutlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { updateActiveTask, Util } from "../../util/Util";
import { mapTasksWithActivation, refreshEmptyActiveTaskDate } from "../../util/taskActivation";
import { loadPendingTasks, persistPendingTasks, revivePendingItems, useSkipFirstRun } from "../../util/pendingTaskStorage";
import ConfirmDialog from "../../components/ConfirmDialog";
import ConversionDetail from "./ConversionDetail";

import { useAuth } from "../../hooks/useAuth";
import { useDepotAccess } from "../../hooks/useDepotAccess";
import { toast } from "react-toastify";
import { ConversionTask, useConversion } from './ConversionContext';
import { useUI } from "../../providers/MoleUIContext";
import { LocalStorageKeys, MYSQL_LIMITS, OperationEnum } from "../../util/constants";

import { Item } from "../../util/Item";
import ConversionItemDetail from "./ConversionItemDetail";
import { useActiveTaskStatusTotal } from "../../hooks/useActiveTaskStatusTotal";
import { v4 } from "uuid";
import { produceFromSource } from "../../util/conversionMath";
import type { ConversionBlocker } from "../../util/conversionMath";

import { addMoney, lineTotal } from "../../util/quantity";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	onSuccess?: () => void;
}

const ConversionModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);
	const [validateItemDetail, setValidateItemDetail] = useState(false);
	const [itemBlockers, setItemBlockers] = useState<ConversionBlocker[]>([]);

	const { api, session, access } = useAuth();
	const { ensureDepotOperation } = useDepotAccess();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { pendingTasks, setPendingTasks, fetchedTask, setFetchedTask, activeTask, addNewTask, setItems, finalQuantity } = useConversion();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});
	const themeColor = useMoleThemeColor();

	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const skipFirstPersist = useSkipFirstRun();

	const { createdAt, updatedAt, active, items: _itms, ...bundleDetails } = activeTask || {};

	const conversionTotal = activeTask
		? lineTotal(finalQuantity, activeTask.dest_cost)
		: 0;

	useActiveTaskStatusTotal(
		conversionTotal,
		null,
		Boolean(isOpen && activeTask),
	);

	const conversionDataLoaded = bundle && activeTask?.id === bundle.batch;

	const detailBlockers = [
		!activeTask?.convType ? { id: "type", key: "conversion.blockers.pickType" } : null,
		!(Number(activeTask?.receiver) > 0) ? { id: "observer", key: "conversion.blockers.pickObserver" } : null,
		!(Number(activeTask?.depot) > 0) ? { id: "depot", key: "conversion.blockers.pickDepot" } : null,
		!activeTask?.date ? { id: "date", key: "conversion.blockers.pickDate" } : null,
	].filter(Boolean) as ConversionBlocker[];
	const blockers = [...detailBlockers, ...itemBlockers.filter((b) => !detailBlockers.some((d) => d.id === b.id))];
	const canValidate = activeTask
		&& blockers.length === 0
		&& validateDetail
		&& validateItemDetail
		&& (bundle ? taskHash !== Util.hashObject(JSON.stringify(bundleDetails)) : true);

	const fetchData = async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`/conversions/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				setFetchedTask(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	};


	const onSubmit = async () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = async () => {
		if (!activeTask) return;

		if (!Util.checkDating(activeTask.date, access, 'conversion')) {
			return;
		}

		if (!ensureDepotOperation(OperationEnum.CONVERSION, activeTask.depot)) {
			return;
		}

		const batch = bundle ? activeTask.id : v4();

		const payload = {
			id: batch,
			receiver: activeTask.receiver,
			depot: activeTask.depot,
			conv_date: activeTask.date,
			ref: activeTask.reference,
			description: activeTask.description,
			type: activeTask.convType,
			source_item: activeTask.source_item,
			source_item_desc: activeTask.source_item_desc,
			dest_item: activeTask.dest_item,
			dest_item_desc: activeTask.dest_item_desc,
			quantity: activeTask.quantity,
			factor: activeTask.factor,
			source_cost: activeTask.source_cost,
			unit_cost: activeTask.dest_cost,
		};

		setLoadingMessage(bundle ? "UPDATE" : "SAVE");

		const res = !bundle ? await api.post("/conversions", payload) : await api.put(`/conversions/${batch}`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				if (bundle) {
					toggle(); // Close modal
					onSuccess?.();
				} else {
					toast.success(t("common:toast.success.conversion"), { autoClose: 3000 });
					deleteTask(activeTask.id); // Remove the active move from pending moves
				}
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
			} else {
				console.error("Failed to send data:", res);
			}
		}

		setLoadingMessage(null);
	}

	const deleteTask = (taskId: string) => {
		setPendingTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
	};

	useEffect(() => {
		if (isOpen) {
			if (bundle) {
				addNewTask();
			} else {
				const parsedTasks = loadPendingTasks(LocalStorageKeys.CONVERSION_TASKS, (task: ConversionTask<Item>) =>
					refreshEmptyActiveTaskDate({
						...task,
						createdAt: new Date(task.createdAt),
						updatedAt: new Date(task.updatedAt),
						produce_qty: Number(task.produce_qty) > 0
							? Number(task.produce_qty)
							: produceFromSource(task.convType, Number(task.quantity) || 1, Number(task.factor) || 1) || 1,
						items: revivePendingItems(task.items, (item) => new Item(item)),
						total: function () {
							return (this.items || []).reduce((sum: number, item: { total: number }) => addMoney(sum, item.total), 0);
						},
					}), session?.user_id);
				if (parsedTasks) {
					setPendingTasks(parsedTasks);
					return;
				}
				addNewTask();
			}
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !bundle) {
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 's') {
					validateBtnRef.current?.click();
				}
			};

			document.addEventListener("keydown", handleKeyDown);

			return () => {
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [isOpen]);

	useEffect(() => {
		if (skipFirstPersist()) return;

		// Always persist pending task when not in edit mode (i.e., when bundle is not provided)
		if (!bundle && isOpen) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(LocalStorageKeys.CONVERSION_TASKS, session?.user_id, JSON.stringify(pendingTasks));
		}

		// If there are pending task and no active task, set the first task as active
		// This ensures that there is always an active task to work with
		// Empty tasks get today's date when activated if their date is stale
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prevTasks) =>
				mapTasksWithActivation(prevTasks, (_task, index) => index === 0)
			);
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);

			// Lazy fetch data if in edit mode and only one pending task exists
			// This is to ensure that the data is fetched only when there is a single task to edit
			// This is because calling it immediately after addNewTask might fetch when there are no tasks yet
			// because setting state is asynchronous
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle) fetchData();
				prevTaskIdRef.current = activeTask.id;
			}

			// If in edit mode, calculate the task hash
			// This is used to determine if the task details have changed
			// and if the update can proceed
			if (conversionDataLoaded && validateItemDetail && !taskHash) {
				setTaskHash(Util.hashObject(JSON.stringify(bundleDetails)));
			}
		} else {
			prevTaskIdRef.current = null;
		}
	}, [activeTask, validateItemDetail]);



	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
		setTaskHash(null);
	};

	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="lg"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxHeight: "95vh" }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={t("common:conversionModal.title")}
					subtitle={activeTask?.description}
					icon={<SwapHorizOutlined />}
					onClose={toggle}
					themeColor={themeColor}
				/>
				<DialogContent>
					<MuiBox>
							{renderChildren &&
								<ConversionDetail
									setValidateCheck={setValidateDetail}
								/>
							}
							{renderChildren &&
								< ConversionItemDetail
									bundle={fetchedTask?.records}
									setValidateCheck={setValidateItemDetail}
									setBlockers={setItemBlockers}
								/>
							}
					</MuiBox>
				</DialogContent>
				<MoleDialogActions sx={{ justifyContent: "stretch" }}>
					<Grid container spacing={1} alignItems="center" width="100%">
						<Grid sx={{ flexGrow: 1, minWidth: 0 }}>
							<TextField
								id="details"
								size="small"
								label={t("common:ui.labels.notes")}
								variant="filled"
								fullWidth
								type="text"
								value={activeTask?.description || ""}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
								onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
								disabled={!activeTask}
							/>
						</Grid>
						<Grid
							sx={{
								width: { xs: "100%", sm: "100%", md: "auto" },
								display: "flex",
								justifyContent: { xs: "space-between", md: "flex-end" },
								alignItems: "center",
								gap: 1,
							}}
						>
							<Stack spacing={0.25} sx={{ mr: 1, maxWidth: 280 }}>
								{!canValidate && blockers.slice(0, 2).map((blocker) => (
									<Typography key={blocker.id} variant="caption" color="error">
										{t(`common:${blocker.key}`, blocker.params)}
									</Typography>
								))}
								{!canValidate && blockers.length === 0 && bundle && (
									<Typography variant="caption" color="text.secondary">
										{t("common:conversion.blockers.noChanges")}
									</Typography>
								)}
							</Stack>
							<Button
								ref={validateBtnRef}
								variant="contained"
								color="success"
								onClick={onSubmit}
								disabled={!canValidate}
								startIcon={<Save />}
							>
								{bundle ? t("common:ui.buttons.amendConversion") : t("common:ui.buttons.convert")}
							</Button>
							<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
						</Grid>
					</Grid>
				</MoleDialogActions>
			</MoleDialog>

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={handleConfirm}
					isOpen={confirmModalOpen}
					setIsOpen={setConfirmModalOpen}
					title={t("common:dialog.conversionConfirmation")}
					message={
						activeTask
							? t("common:conversion.confirmPreview", {
								sourceQty: Util.formatNumber(Number(activeTask.quantity) || 1),
								source: activeTask.source_item_desc,
								destQty: Util.formatNumber(Number(activeTask.produce_qty) || 1),
								dest: activeTask.dest_item_desc,
							})
							: undefined
					}
					confirmBtnColor="success" />
			}
		</>
	);
};

export default ConversionModal;
